import {
  type SelectProps,
  type SelectTriggerProps,
  SelectValue,
} from "@radix-ui/react-select";
import { ChevronDown, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { TreeView } from "@/components/tree-view";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { findNameById } from "@/utils/findNameById";

import type { ItemsListType } from "./types";

export interface BaseSelectProps extends Partial<SelectProps> {
  itemsList: ItemsListType;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  triggerProps?: Partial<SelectTriggerProps> & { "data-testid"?: string };
  className?: string;
  isTree?: boolean;
  hasError?: boolean;
  isSearchable?: boolean;
  isShowEmpty?: boolean;
}

const BaseSelect = ({
  itemsList,
  value,
  onChange,
  placeholder = "",
  triggerProps,
  className = "w-full",
  isTree = false,
  hasError = false,
  disabled,
  isSearchable,
  isShowEmpty = false,
  ...selectProps
}: BaseSelectProps) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (isSearchable && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchTerm, isSearchable]);

  const filteredItems = itemsList.filter(item => {
    const isMatched = item.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const isSelected = item.id === value;
    return isMatched || isSelected;
  });

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) setSearchTerm("");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (disabled && open) {
      setOpen(false);
    }
  }, [disabled, open]);

  const isEmpty = itemsList.length === 0 && isShowEmpty;

  if (isTree) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          {...triggerProps}
          disabled={disabled}
          onClick={() => {
            if (!disabled) {
              setOpen(o => !o);
            }
          }}
          className={cn(
            "h-10 px-3 border border-gray-200 rounded-[6px] text-sm bg-white disabled:bg-gray-100 disabled:opacity-50 flex items-center justify-between",

            className
          )}
        >
          <span className="text-left w-full">
            {value ? (
              findNameById(itemsList, value)
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
          </span>
          <ChevronDown className="h-4 w-4 opacity-30" />
        </button>

        {open && !disabled && (
          <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto border border-gray-200 rounded bg-white shadow">
            <TreeView
              data={itemsList}
              initialSelectedItemId={value}
              onSelectChange={item => {
                if (item) {
                  onChange?.(item.id);
                  if (!item.children || item.children.length === 0) {
                    setOpen(false);
                  }
                }
              }}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <Select
      value={value}
      onValueChange={onChange}
      {...selectProps}
      onOpenChange={handleOpenChange}
    >
      <SelectTrigger
        {...triggerProps}
        disabled={disabled}
        data-testid={triggerProps?.["data-testid"]}
        className={cn(
          "h-10 px-3 border border-gray-200 rounded-[6px] text-sm shadow-none bg-white pr-2 disabled:bg-gray-100 disabled:opacity-50!",
          hasError && "border-red-500",
          className
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-60 overflow-y-auto">
        {isSearchable && (
          <div className="sticky -top-1 z-10 bg-white py-1 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                ref={inputRef}
                placeholder="Search..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-8 h-9 border-none shadow-none focus-visible:ring-[1px]"
                onKeyDown={e => e.stopPropagation()}
              />
            </div>
          </div>
        )}

        <SelectGroup>
          {filteredItems.length > 0 ? (
            filteredItems.map((item, i) => (
              <SelectItem
                key={`${item.id}-${i}`}
                value={item.id}
                disabled={item.disabled}
              >
                {item.name}
              </SelectItem>
            ))
          ) : (
            <div className="py-6 text-center text-sm text-gray-500">
              Not found
            </div>
          )}
          {isEmpty && (
            <div className="py-3 flex items-center justify-center text-xs text-muted-foreground pointer-events-none">
              Not found
            </div>
          )}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

export default BaseSelect;
